"use client";

import Link from "next/link";
import { Sparkles, ArrowUpRight } from "lucide-react";
import { useT } from "@/hooks/useT";

/**
 * Share-to-Earn promo card for the /activity right rail.
 *
 * Iteration 3 — fixes the floating-orphan "10%" + "of every platform
 * fee" alignment that was bothering the eye in iteration 2. The hero
 * stat now lives inside a tighter pill block where the number and the
 * supporting copy share a baseline / column relationship instead of
 * one being giant and the other orphaned to the right.
 *
 * Layout:
 *  - Header row: Sparkle icon + "Share to Earn" label + NEW pill
 *  - Stat block: "Earn 10%" as a single readable line ("Earn" small,
 *    "10%" hero in lime), then "of every fee from your posts" sub
 *  - Steps: tight three-line list with lime dot bullets (no chunky
 *    01/02/03 numerals)
 *  - CTA: ghost link "Share a trade ↗" with hover lime tint
 *
 * Brand presence stays via the lime "10%" + the lime dots; everything
 * else is dark glass + neutral gray. Reads premium without yelling.
 */
export default function ShareToEarnPromoCard() {
  const t = useT();
  return (
    <div className="rounded-[5px] bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] overflow-hidden">
      {/* Header row */}
      <div className="flex items-center justify-between px-5 pt-5">
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center justify-center w-6 h-6 rounded-[5px] bg-gray-100 dark:bg-white/[0.06]">
            <Sparkles className="w-3.5 h-3.5 text-gray-600 dark:text-gray-300" strokeWidth={2.4} />
          </div>
          <span className="text-[10px] font-inter-tight font-bold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
            {t("activity.shareToEarn.title")}
          </span>
        </div>
        <span className="inline-flex items-center px-1.5 py-0.5 rounded-[5px] bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300">
          <span className="text-[9px] font-inter-tight font-bold uppercase tracking-wider">{t("activity.shareToEarn.newBadge")}</span>
        </span>
      </div>

      {/* Hero stat — single legible line "Earn 10% of fees …".
          Flex with `items-baseline` so the giant lime "10%" actually
          sits on the same baseline as the small label text — fixes the
          orphan alignment from the previous version. */}
      <div className="px-5 pt-4 pb-5">
        <div className="flex items-baseline gap-2 mb-2">
          <span className="text-[15px] text-gray-500 dark:text-gray-400 font-satoshi">{t("activity.shareToEarn.earn")}</span>
          <span className="font-monument text-[42px] leading-none tracking-tight text-gray-900 dark:text-white">
            10%
          </span>
          <span className="text-[15px] text-gray-500 dark:text-gray-400 font-satoshi">{t("activity.shareToEarn.ofFees")}</span>
        </div>
        <p className="text-[13px] text-gray-600 dark:text-gray-300 leading-relaxed">
          {t("activity.shareToEarn.tagline.before")}{" "}
          <span className="font-semibold text-gray-900 dark:text-white">{t("activity.shareToEarn.tagline.yourPost")}</span>{t("activity.shareToEarn.tagline.after")}
        </p>
      </div>

      {/* Steps — compact, single-line each. Lime dots replace the
          monument 01/02/03 numerals which felt heavy on a small card.
          Three rules of thumb you can scan in under a second. */}
      <div className="px-5 py-4 border-t border-gray-100 dark:border-[#1A1A1A] space-y-2.5">
        {[
          t("activity.shareToEarn.step1"),
          t("activity.shareToEarn.step2"),
          t("activity.shareToEarn.step3"),
        ].map((step, i) => (
          <div key={i} className="flex items-start gap-2.5">
            <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-gray-400 dark:bg-gray-500 mt-[7px]" />
            <span className="text-[12.5px] text-gray-700 dark:text-gray-300 leading-snug">
              {step}
            </span>
          </div>
        ))}
      </div>

      {/* CTA — white-solid (2026-06-09): lime retired from CTAs per brand
          call, matches the rest of the app's primary buttons. */}
      <div className="px-5 pt-3.5 pb-4 border-t border-gray-100 dark:border-[#1A1A1A]">
        <Link
          href="/markets"
          className="flex items-center justify-center gap-1.5 w-full px-4 py-2 rounded-[5px] bg-gray-900 dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-100 text-white dark:text-gray-900 text-[13px] font-satoshi font-bold transition-colors group"
        >
          <span>{t("activity.shareToEarn.cta")}</span>
          <ArrowUpRight className="w-4 h-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </Link>
      </div>
    </div>
  );
}
